import React from 'react';
import { assertDefined } from '../../lib/util';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import type { OffHireEntry } from '@seaber/common/core/entries';
import { Modal, ModalBody } from '../ui/index';
import type { ModalProps } from '../ui/Modal/Modal';
import OffHireForm from './OffHireForm';

type OffHireModalContentProps = {
  vesselId?: number;
  offHire?: OffHireEntry;
  initialStartTime?: number;
  scheduleContext?: ScheduleDataContext;
  onClose: () => void;
  onSave?: (offHire: OffHireEntry) => void;
};

export type OffHireModalProps = OffHireModalContentProps & Partial<ModalProps>;

const EditOffHireModalContent = (
  props: OffHireModalContentProps &
    Required<
      Pick<OffHireModalContentProps, 'vesselId' | 'offHire' | 'scheduleContext'>
    >,
): JSX.Element => {
  const { onSave, onClose } = props;

  if (!onSave) {
    throw new Error(`onSave must be defined`);
  }

  return (
    <OffHireForm
      {...props}
      offHire={props.offHire}
      onCancel={onClose}
      onSave={(offHire) => {
        onSave(offHire);
        props.onClose();
      }}
    />
  );
};

const CreateOffHireModalContent = (
  props: OffHireModalContentProps &
    Required<Pick<OffHireModalContentProps, 'vesselId' | 'scheduleContext'>>,
): JSX.Element => {
  const { onSave, onClose, scheduleContext } = props;

  if (!onSave) {
    throw new Error(`onSave must be defined`);
  }
  assertDefined(
    scheduleContext,
    `scheduleContext should be defined for OffHireModal`,
  );

  return (
    <OffHireForm
      {...props}
      scheduleContext={scheduleContext}
      offHire={null}
      onCancel={onClose}
      onSave={(offHire) => {
        onSave(offHire);
        props.onClose();
      }}
    />
  );
};

const OffHireModal = ({
  offHire,
  onClose,
  vesselId,
  scheduleContext,
  ...restProps
}: OffHireModalProps): JSX.Element | null => {
  if (vesselId === undefined || scheduleContext === undefined) {
    return null;
  }

  return (
    <Modal isOpen onClose={onClose} {...restProps} height="32.5em" width="50em">
      <ModalBody p={0} overflowX="visible" overflowY="visible">
        {offHire !== undefined ? (
          <EditOffHireModalContent
            vesselId={vesselId}
            offHire={offHire}
            scheduleContext={scheduleContext}
            {...restProps}
            onClose={onClose}
          />
        ) : (
          <CreateOffHireModalContent
            vesselId={vesselId}
            scheduleContext={scheduleContext}
            {...restProps}
            onClose={onClose}
          />
        )}
      </ModalBody>
    </Modal>
  );
};

export default OffHireModal;
